<template>
  <SogPage
    id="page"
    :title="_title"
  >
    <OrderHeadCardComponent
      id="orderHeadCard"
      :order-head="_orderHead"
      :payment="_payment"
    >
      <SogCardContent v-show="_progressInfo">
        <SogProgressInfoComponent
          :progress-info="_progressInfo"
          :show-caption="false"
        />
      </SogCardContent>
      <SogCardActions>
        <SogCardActionButton
          :kind="SogButtonKind.Refresh"
          @click="onLoad"
        />
        <SogCardActionButton
          mdi-icon="mdi-printer"
          @click="onPrint"
        />
        <SogCardActionButton
          v-show="_paymentOrderVisible"
          mdi-icon="mdi-bank-transfer-out"
          tooltip="Auszahlung anornden"
          @click="onPaymentOrder"
        />
        <SogCardActionDropDownButton
          v-show="_printExportBlankVisible"
          mdi-icon="mdi-file-pdf-box"
          :items="_menuPrintExportBlank"
          @select="onPrintExportBlank"
        >
          Vordruck
        </SogCardActionDropDownButton>
        <SogCardActionButton
          v-show="_insuranceVisible"
          mdi-icon="mdi-shield"
          @click="onShowInsurance"
        >
          Versicherungskarten
        </SogCardActionButton>
        <SogCardActionButton
          v-show="_shippingVisible"
          mdi-icon="mdi-truck-delivery"
          tooltip="Die Bestellung versenden"
          @click="onShipping"
        />
        <SogCardActionButton
          v-show="_shippingVisible"
          mdi-icon="mdi-truck-fast"
          tooltip="Die Bestellung direkt im Lager des Empfängers ablegen."
          @click="onShippingAndDelivery"
        />
        <SogCardActionButton
          v-show="_deliveryVisible"
          mdi-icon="mdi-truck-check"
          tooltip="Die Bestellung annehmen"
          @click="onDelivery"
        />
        <SogCardActionButton
          v-show="_orderPayVisible&&!_orderHead.paid"
          mdi-icon="mdi-currency-eur"
          tooltip="Zahlung"
          @click="onShowPaymentDialog"
        />
        <SogCardActionButton
          v-show="_orderPayVisible&&_orderHead.paid"
          mdi-icon="mdi-currency-eur-off"
          tooltip="Zahlung löschen"
          @click="onDeletePayment"
        />
        <SogCardActionButton
          v-show="isBranch"
          mdi-icon="mdi-cart-plus"
          tooltip="Einzeleinkauf"
          @click="p_onPurchase"
        />
        <SogCardActionMoreButton
          :items="menuItems"
          @select="onMoreSelect"
        />
        <SogCardActionButton
          :kind="SogButtonKind.Back"
          @click="onBack"
        />
      </SogCardActions>
    </OrderHeadCardComponent>
    <SogCard>
      <SogCardTitle>Positionen</SogCardTitle>
      <SogCardContent>
        <SogGrid
          ref="_grid"
          :data="_orderItems"
          :columns="_columns"
          :allow-paging="false"
        />
      </SogCardContent>
    </SogCard>
    <SogCard v-show="_history.length>0">
      <SogCardTitle>Historie</SogCardTitle>
      <SogCardContent>
        <HistoryListComponent :data="_history" />
      </SogCardContent>
    </SogCard>
    <PaymentComponent
      ref="_paymentDialog"
      :order-head="_orderHead"
      @ok-clicked="onPayment"
    />
  </SogPage>
</template>

<script setup lang="ts">
import SogPage from "@/shared/ui/SogPage.vue";
import {useNavigationStore} from "@/modules/Navigation/store";
import {computed, onMounted, reactive, ref, watch} from "vue";
import OrderHead, {OrderStatus, OrderType} from "@/shared/model/orderHead";
import copyTo from "@/shared/utils/CopyTo";
import SogCard from "@/shared/ui/SogCard.vue";
import SogCardTitle from "@/shared/ui/SogCardTitle.vue";
import SogCardContent from "@/shared/ui/SogCardContent.vue";
import SogGrid from "@/shared/ui/SogGrid.vue";
import OrderItem from "@/shared/model/orderItem";
import {ColumnModel} from "@syncfusion/ej2-grids/src/grid/models/column";
import {DefaultColumn, MoneyColumn, NumberColumn} from "@/shared/ui/types/SogGridColumnDefinitions";
import SogCardActionButton from "@/shared/ui/SogCardActionButton.vue";
import apiFile from "@/shared/services/apiFile";
import apiOrder from "@/modules/Order/api";
import {onBeforeRouteUpdate, RouteLocationNormalizedLoaded, useRoute, useRouter} from "vue-router";
import OrderHeadCardComponent from "@/modules/Order/components/OrderHeadCardComponent.vue";
import {useSpinner} from "@/shared/composables/SogSpinner";
import {useAccountStore} from "@/stores/accountStore";
import {SogButtonKind} from "@/shared/ui/types/SogButtonKind";
import apiHistory from "@/modules/History/api";
import History from "@/shared/model/History";
import {HistoryAction} from "@/shared/model/HistoryAction";
import notificationService from "@/shared/services/NotificationService";
import HistoryListComponent from "@/modules/History/components/HistoryListComponent.vue";
import SogCardActions from "@/shared/ui/SogCardActions.vue";
import SogCardActionMoreButton, {IActionItem} from "@/shared/ui/SogCardActionMoreButton.vue";
import {mdiFileExcelBox} from "@mdi/js";
import {ProgressInfo} from "@/shared/model/ProgressInfo";
import progressService from "@/shared/services/ProgressService";
import SogProgressInfoComponent from "@/shared/ui/SogProgressInfoComponent.vue";
import {IdType} from "@/shared/model/idType";
import PaymentComponent from "@/modules/Order/components/PaymentDialogComponent.vue";
import {storeToRefs} from "pinia";
import {isInsurance, ProductType} from "@/shared/model/productType";
import SogCardActionDropDownButton from "@/shared/ui/SogCardActionDropDownButton.vue";
import SogDropDownButtonItem from "@/shared/ui/types/SogDropDownButtonItem";
import Payment from "@/shared/model/Payment";


interface Props {
  id?: string
}

const props = withDefaults(defineProps<Props>(), {
    id: undefined
  }
)


const _navigationStore = useNavigationStore();

const _spinner = useSpinner()


const _accountStore = useAccountStore();
const {isBranch} = storeToRefs(_accountStore)
const _isPriceVisible = computed(() => {
  return !(isBranch.value)
})

// eslint-disable-next-line no-undef
const _title = ref("")

const currentOrderId = ref<string | undefined>(undefined)


const _orderHead: OrderHead = reactive<OrderHead>(new OrderHead())
const _orderItems: OrderItem[] = reactive<OrderItem[]>([])
const _payment: Payment = reactive<Payment>(new Payment())
const _history: History[] = reactive<History[]>([])
const _progressInfo = ref<ProgressInfo | undefined>(undefined)

const menuItems: IActionItem[] = [
  {
    text: 'Export',
    id: "excelExport",
    svgIcon: mdiFileExcelBox,
    visible: true
  },
]

const _menuPrintExportBlank: SogDropDownButtonItem[] = [
  {id: "current", text: (new Date().getFullYear()).toString()},
  {separator: true},
  {id: "next", text: (new Date().getFullYear() + 1).toString()},
  {id: "none", text: "Ohne"}];

/*
const menu = ref<InstanceType<typeof ContextMenuComponent> | null>(null)
const btnMore = ref<InstanceType<typeof SogCardActionButton> | null>(null)
*/


const _columns: ColumnModel[] = [
  /*
  { type: 'checkbox' },
*/
  {...DefaultColumn, field: 'id', headerText: 'id', isPrimaryKey: true, visible: false},
  {...DefaultColumn, field: 'description', headerText: 'Beschreibung', visible: true, width: 300, autoFit: false},
  {...MoneyColumn, field: 'productGrossPrice', headerText: 'Bruttoprämie,€'},
  {...MoneyColumn, field: 'productCommission', headerText: 'Courtage,€', visible: _isPriceVisible.value},
  {...NumberColumn, field: 'count', headerText: 'Anzahl', visible: true},
  {...MoneyColumn, field: 'taxAmount', headerText: 'VersSt,€', visible: _isPriceVisible.value},
  {...MoneyColumn, field: 'itemAmount', headerText: 'Zahlungsbetrag,€', visible: _isPriceVisible.value},
]

const _grid = ref<InstanceType<typeof SogGrid> | null>(null)
const _router = useRouter()
const _stack: string[] = []


//_accountStore.user.personId
//_orderHead.orderType==='Invoice' && !_orderHead.paid
const _orderPayVisible = computed(() => {
  return _orderHead.orderType === OrderType.Invoice && _orderHead.supplierId === _accountStore.person?.id
})

const _shippingVisible = computed(() => {
  return _orderHead.orderType === OrderType.Order && _orderHead.supplierId === _accountStore.person?.id && _orderHead.statusId === OrderStatus.Completed &&
    _history && _history.every((t) => t.action !== HistoryAction.OrderReceived)
})

const _deliveryVisible = computed(() => {
  return _orderHead.orderType === OrderType.Order && _orderHead.personId === _accountStore.person?.id && _orderHead.statusId === OrderStatus.Sended
})

const _insuranceVisible = computed(() => {
  return _orderItems.some(t => isInsurance(t.productTypeId))
})

const _printExportBlankVisible = computed(() => {
  return _accountStore.isRoot && _orderItems.some(t => t.productTypeId === ProductType.ExportBlank)
})


const _paymentOrderVisible = computed(()=>{
  return _accountStore.isRoot && _orderHead.orderType === OrderType.Invoice && _orderHead.orderAmount && _orderHead.orderAmount<0 && !_orderHead.paid
})

watch((props), () => {
  currentOrderId.value = props.id
}, {immediate: true})

async function onPrintExportBlank(item: SogDropDownButtonItem) {
  try {
    _spinner.show()
    const t_currentYear = item.id === "current" ? true : item.id === "next" ? false : undefined;
    const id = await apiOrder.orderInitPrintExport(currentOrderId.value, t_currentYear)
    if (id) {
      await apiFile.downloadFile(id, _orderHead.orderNumber + "_vordruck.pdf")
    }
  } finally {
    _spinner.hide()
  }

}

async function load(id: string) {
  _spinner.show()
  _orderItems.length = 0;
  copyTo(new OrderHead(), _orderHead)
  copyTo(new Payment(), _payment)

  const order = await apiOrder.orderLoad(id)
  if (order) {
    copyTo(order.orderHead, _orderHead)
    copyTo(order.payment, _payment)
    _orderItems.length = 0
    _title.value = _orderHead.orderType === OrderType.Invoice ? "Rechnung" : _orderHead.orderType === OrderType.Order ? "Bestellung" : ""
    order.orderItems.map(t => _orderItems.push(t))
    _grid.value?.refresh();
    _history.length = 0
    const lst = await apiHistory.listOrder(id)
    if (lst) {
      lst.map(t => _history.push(t))
    }
  }
  _spinner.hide()

}

onMounted(async () => {


  if (!currentOrderId.value) {
    _router.back();
    return;
  }
  await load(currentOrderId.value)
})

async function onPrint() {
  try {
    _spinner.show()
    const id = await apiOrder.orderInitPdf(currentOrderId.value)
    if (id) {
      await apiFile.downloadFile(id, _orderHead.orderNumber + ".pdf")
    }
  } finally {
    _spinner.hide()
  }
}


watch(currentOrderId, async (newValue, oldValue) => {
  if (newValue) {
    if (oldValue) {
      _stack.push(oldValue)
    }
    await load(newValue)
  }
})


function onShowInsurance() {
  _navigationStore.showInsuranceList(IdType.Order, currentOrderId.value, _orderHead.orderNumber)
}

async function onShipping() {
  if (_history && _history.some((t) => t.action === HistoryAction.OrderSended)) {
    notificationService.showYesNo("", "Bestellung wurde schon versendet. Wollen Sie noch mal senden?", async () => {
      await doShipping();
    })
  } else {
    await doShipping();
  }
}

async function onShippingAndDelivery() {
  let t_shipping = false
  if (_history && _history.some((t) => t.action === HistoryAction.OrderSended)) {
    notificationService.showYesNo("", "Bestellung wurde schon versendet. Wollen Sie noch mal senden?", async () => {
      t_shipping = true;
    })
  } else {
    t_shipping = true;
  }
  if (t_shipping) {
    await doShipping();
    await onDelivery();
  }
}


async function doShipping() {
  if (!currentOrderId.value)
    return
  try {
    _spinner.show()
    if (await apiOrder.shipping(currentOrderId.value))
      await load(currentOrderId.value)
  } finally {
    _spinner.hide()
  }
}

async function onDelivery() {
  if (!currentOrderId.value)
    return
  try {
    _spinner.show()
    if (await apiOrder.delivery(currentOrderId.value))
      await load(currentOrderId.value)
  } finally {
    _spinner.hide()
  }
}

async function onMoreSelect(id: string) {
  if (id === "excelExport") {
    _spinner.show()
    const id = await apiOrder.initExportExcel(currentOrderId.value)
    if (id) {
      await progressService.addToGroup([id], false, onRefresh)
    }
    _spinner.hide()
  }
}

async function onRefresh(group: string, progressInfo: ProgressInfo) {
  _progressInfo.value = progressInfo
  if (progressInfo) {
    if (progressInfo.finishDate) {
      await progressService.removeFromGroup([progressInfo.groupId ?? ""], onRefresh)
      if (!progressInfo.successful) {
        notificationService.error(progressInfo.resultMessage ?? "")
      } else {
        if (progressInfo.resultId) {
          await apiFile.downloadFile(progressInfo.resultId)
        }
      }
      _progressInfo.value = undefined
    }
  }
}

const _paymentDialog = ref<InstanceType<typeof PaymentComponent> | null>(null)

function onShowPaymentDialog() {
  _paymentDialog.value?.showModal()
}

function onPayment() {
  load(_orderHead?.id ?? "")
}

function onLoad() {
  load(_orderHead?.id ?? "")
}

async function sendPaymentOrder() {
    _spinner.show()

    try {
      if (await apiOrder.orderPayment(_orderHead.id ?? "")) {
        notificationService.success("Auszahlung wurde angeordnet")
        await load(currentOrderId.value ?? "")
      }
    } finally {
      _spinner.hide()

    }
}


async function onPaymentOrder() {
  if (_history.some(t => t.action === HistoryAction.CreateOrderPaymentEmail)) {
    notificationService.showYesNo("Auszahlung anordnen.",
        "Die Auszahlung wurde bereits angeordnet. Möchten Sie sie erneut anordnen?",
        async () => {
          await sendPaymentOrder();
        })
  } else {
    await sendPaymentOrder();
  }

}

function onDeletePayment() {
  notificationService.showYesNo("Zahlung Löschen", "Wollen Sie die Zahlung löschen?", async () => {
    try {
      _spinner.show()
      if (await apiOrder.payRemove(_orderHead.id ?? "")) {
        await load(currentOrderId.value ?? "")
        notificationService.success("Zahlung wurde gelöscht")
      }
    } finally {
      _spinner.hide()
    }
  })
}

function onBack() {
//
}

async function p_onPurchase() {
  await _router.push("/shoppingcartsingleproduct/purchase/producttype");
}

/*
function p_initRouteFrom(from: RouteLocationNormalizedLoaded) {
  console.log(from)
}

onBeforeRouteUpdate((to, from) => {
  console.log("onBeforeRouteUpdate")
  p_initRouteFrom(from)
  // from = woher
  // to = wohin
})
*/
</script>
<style>
</style>
